// FINAL — src/pages/Subjects.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpenIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  showSuccess,
  showError,
  showWarning,
  showConfirm,
  showInfo,
} from '../utils/alert';
import { useAuth } from '../context/AuthContext';

export default function Subjects() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not admin or super_admin
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin') {
      navigate('/app/dashboard');
    }
  }, [user, navigate]);

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [newSubject, setNewSubject] = useState({
    name: '',
    code: '',
    description: '',
    level: 'primary',
    stream: 'general',
    subject_type: 'core',
    is_compulsory: false,
    is_active: true,
  });

  const [adding, setAdding] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [checkingClassSubjects, setCheckingClassSubjects] = useState(false);
  const [selectedClassSubjects, setSelectedClassSubjects] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSubjects();
    fetchClasses();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/subjects');

      let data = res.data;
      if (data?.data) data = data.data;
      else if (data?.rows) data = data.rows;

      setSubjects(Array.isArray(data) ? data : []);
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      await showError({ text: 'Failed to load subjects' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await apiClient.get('/classes');
      let data = res.data;
      if (data?.data) data = data.data;
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = subjects.map((s) => s.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const emptySubjectForm = {
    name: '',
    code: '',
    description: '',
    level: 'primary',
    stream: 'general',
    subject_type: 'core',
    is_compulsory: false,
    is_active: true,
  };

  const openAddSubjectModal = () => {
    setEditingSubject(null);
    setNewSubject(emptySubjectForm);
    setShowAddModal(true);
  };

  const openEditSubjectModal = (subject) => {
    setEditingSubject(subject);
    setNewSubject({
      name: subject.name || '',
      code: subject.code || '',
      description: subject.description || '',
      level: subject.level || 'primary',
      stream: subject.stream || 'general',
      subject_type: subject.subject_type || 'core',
      is_compulsory: Boolean(subject.is_compulsory),
      is_active: subject.is_active !== false,
    });
    setShowAddModal(true);
  };

  const closeSubjectModal = () => {
    if (adding) return;
    setShowAddModal(false);
    setEditingSubject(null);
    setNewSubject(emptySubjectForm);
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.name.trim()) {
      await showWarning({ title: 'Missing Name', text: 'Subject name is required.' });
      return;
    }

    setAdding(true);

    if (editingSubject) {
      try {
        const response = await apiClient.put(
          `/subjects/${editingSubject.id}`,
          newSubject,
        );
        const updated = response.data?.data || response.data;
        setSubjects((current) =>
          current.map((subject) =>
            subject.id === editingSubject.id
              ? { ...subject, ...newSubject, ...updated }
              : subject,
          ),
        );
        setAdding(false);
        closeSubjectModal();
        await showSuccess({
          title: 'Subject Updated',
          text: `${newSubject.name} was updated successfully.`,
        });
      } catch (error) {
        await showError({
          text: error.response?.data?.message || 'Failed to update subject',
        });
      } finally {
        setAdding(false);
      }
      return;
    }

    setShowAddModal(false);

    const tempId = Date.now();
    const optimisticSubject = {
      id: tempId,
      ...newSubject,
      created_at: new Date().toISOString(),
    };
    setSubjects((prev) => [optimisticSubject, ...prev]);

    try {
      const res = await apiClient.post('/subjects', newSubject);
      const createdSubject = res.data.data || res.data;
      setSubjects((prev) =>
        prev.map((s) => (s.id === tempId ? { ...s, ...createdSubject, id: createdSubject.id } : s))
      );
      await showSuccess({ text: 'Subject added successfully!', toast: true });
      setNewSubject(emptySubjectForm);
    } catch (error) {
      setSubjects((prev) => prev.filter((s) => s.id !== tempId));
      await showError({ text: error.response?.data?.message || 'Failed to add subject' });
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteSubject = async (subject) => {
    const confirmed = await showConfirm({
      title: 'Delete Subject',
      text: `Are you sure you want to delete "${subject.name}"? This action cannot be undone.`,
      confirmText: 'Yes, delete',
      icon: 'error',
    });
    if (!confirmed) return;

    setDeleting(true);
    const previousSubjects = [...subjects];
    setSubjects((prev) => prev.filter((s) => s.id !== subject.id));

    try {
      await apiClient.delete(`/subjects/${subject.id}`);
      await showSuccess({ text: 'Subject deleted successfully!', toast: true });
    } catch (error) {
      setSubjects(previousSubjects);
      await showError({ text: error.response?.data?.message || 'Cannot delete subject (it may be assigned to classes)' });
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    const confirmed = await showConfirm({
      title: 'Bulk Delete',
      text: `Are you sure you want to delete ${selectedIds.length} selected subject(s)? This action cannot be undone.`,
      confirmText: 'Yes, delete all',
      icon: 'error',
    });
    if (!confirmed) return;

    setBulkDeleting(true);
    const previousSubjects = [...subjects];
    setSubjects((prev) => prev.filter((s) => !selectedIds.includes(s.id)));
    const idsToDelete = [...selectedIds];
    setSelectedIds([]);

    try {
      await apiClient.delete('/subjects/bulk-delete', {
        data: { ids: idsToDelete },
      });
      await showSuccess({ text: `${idsToDelete.length} subject(s) deleted successfully`, toast: true });
    } catch (error) {
      setSubjects(previousSubjects);
      await showError({ text: error.response?.data?.message || 'Bulk delete failed' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const openAssignModal = (subject) => {
    setSelectedSubject(subject);
    setSelectedClassId('');
    setSelectedClassSubjects([]);
    setShowAssignModal(true);
  };

  const handleAssignmentClassChange = async (classId) => {
    setSelectedClassId(classId);
    setSelectedClassSubjects([]);
    if (!classId) return;

    setCheckingClassSubjects(true);
    try {
      const response = await apiClient.get(
        `/subjects/classes/${classId}/subjects`,
      );
      const data = response.data?.data || response.data || [];
      setSelectedClassSubjects(Array.isArray(data) ? data : []);
    } catch (error) {
      await showError({
        text:
          error.response?.data?.message ||
          'Unable to check the subjects currently assigned to this class',
      });
    } finally {
      setCheckingClassSubjects(false);
    }
  };

  const subjectAlreadyAssigned = selectedClassSubjects.some(
    (subject) => Number(subject.id) === Number(selectedSubject?.id),
  );

  const handleAssignToClass = async () => {
    if (!selectedClassId) {
      await showWarning({ title: 'No Class', text: 'Please select a class.' });
      return;
    }

    setAssigning(true);
    try {
      const response = await apiClient.post('/subjects/assign', {
        class_id: selectedClassId,
        subject_ids: [selectedSubject.id],
      });
      await showSuccess({
        title: subjectAlreadyAssigned ? 'Already Assigned' : 'Subject Assigned',
        text:
          response.data?.message ||
          `"${selectedSubject.name}" was assigned successfully.`,
      });
      setShowAssignModal(false);
    } catch (error) {
      await showError({ text: error.response?.data?.message || 'Failed to assign subject' });
    } finally {
      setAssigning(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/subjects/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subject_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      await showSuccess({ text: 'Template downloaded successfully', toast: true });
    } catch (error) {
      await showError({ text: 'Failed to download template' });
      console.error(error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type)) {
      await showWarning({ title: 'Invalid File', text: 'Please upload an Excel file (.xlsx or .xls)' });
      fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingBulk(true);
    try {
      const res = await apiClient.post('/subjects/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await showSuccess({ text: res.data.message || 'Subjects uploaded successfully', toast: true });
      if (res.data.errors && res.data.errors.length > 0) {
        const errorMessages = res.data.errors.map(
          (err) => `Row ${err.row}: ${err.errors.join(', ')}`
        );
        await showWarning({ title: 'Some rows had errors', text: errorMessages.join('\n') });
      }
      await fetchSubjects();
    } catch (error) {
      const errMsg = error.response?.data?.message || 'Upload failed';
      await showError({ text: errMsg });
      if (error.response?.data?.errors) {
        const details = error.response.data.errors
          .map((e) => `Row ${e.row}: ${e.errors.join(', ')}`)
          .join('\n');
        await showError({ text: `Validation errors:\n${details}` });
      }
    } finally {
      setUploadingBulk(false);
      fileInputRef.current.value = '';
    }
  };

  // Compute stats
  const totalSubjects = subjects.length;
  const activeSubjects = subjects.filter(s => s.is_active !== false).length;
  const coreSubjects = subjects.filter(s => s.subject_type === 'core').length;
  const electiveSubjects = subjects.filter(s => s.subject_type === 'elective').length;

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg p-5 sm:p-8 text-white">
        <div className="absolute right-0 top-0 opacity-10">
          <BuildingOfficeIcon className="w-32 sm:w-48 h-32 sm:h-48" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold leading-tight">
              Subjects
            </h1>
            <p className="text-sm sm:text-base text-white/80 mt-1">
              Manage all subjects offered in your school
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Bulk Delete Button */}
            {selectedIds.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all disabled:opacity-50 shadow-sm hover:shadow-md text-sm"
              >
                {bulkDeleting ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5 sm:mr-2" />
                ) : (
                  <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                )}
                <span className="hidden xs:inline">Delete Selected</span>
                <span className="xs:hidden">Delete</span>
                <span className="ml-1">({selectedIds.length})</span>
              </button>
            )}

            {/* Download Template Button */}
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all text-sm"
            >
              <ArrowDownTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span className="hidden xs:inline">Template</span>
              <span className="xs:hidden">Template</span>
            </button>

            {/* Upload Excel Button */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              disabled={uploadingBulk}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingBulk}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all disabled:opacity-50 text-sm"
            >
              {uploadingBulk ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5 sm:mr-2" />
                  <span className="hidden xs:inline">Uploading...</span>
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span className="hidden xs:inline">Upload Excel</span>
                  <span className="xs:hidden">Upload</span>
                </>
              )}
            </button>

            {/* Add New Subject Button */}
            <button
              onClick={openAddSubjectModal}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-white text-primary-700 rounded-xl hover:bg-gray-100 transition-all shadow-sm hover:shadow-md text-sm font-medium"
            >
              <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span className="hidden xs:inline">Add Subject</span>
              <span className="xs:hidden">Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-xl bg-blue-50 text-blue-600">
              <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Total Subjects</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{totalSubjects}</p>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
            <span className="text-[10px] sm:text-xs text-gray-400">All subjects</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-xl bg-green-50 text-green-600">
              <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Active</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{activeSubjects}</p>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
            <span className="text-[10px] sm:text-xs text-gray-400">{totalSubjects > 0 ? Math.round((activeSubjects / totalSubjects) * 100) : 0}% of total</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-purple-200 transition-colors shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-xl bg-purple-50 text-purple-600">
              <ClipboardDocumentListIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Core</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{coreSubjects}</p>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
            <span className="text-[10px] sm:text-xs text-gray-400">Compulsory subjects</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-yellow-200 transition-colors shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-xl bg-yellow-50 text-yellow-600">
              <ExclamationTriangleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Elective</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{electiveSubjects}</p>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
            <span className="text-[10px] sm:text-xs text-gray-400">Optional subjects</span>
          </div>
        </div>
      </div>

      {/* Subjects Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {subjects.length === 0 ? (
          // Beautiful Empty State
          <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
            <div className="relative">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-primary-50 flex items-center justify-center mb-4 sm:mb-6">
                <BookOpenIcon className="w-12 h-12 sm:w-16 sm:h-16 text-primary-300" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-secondary-100 flex items-center justify-center shadow-sm">
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-1">No Subjects Yet</h3>
            <p className="text-sm sm:text-base text-gray-400 text-center max-w-sm mb-6">
              Start building your curriculum by adding subjects. You can also import from an Excel file.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={openAddSubjectModal}
                className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm hover:shadow-md text-sm sm:text-base"
              >
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Add First Subject
              </button>
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <ArrowDownTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Download Template
              </button>
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                disabled={uploadingBulk}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingBulk}
                className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                <ArrowUpTrayIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Upload Excel
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle px-4 sm:px-0">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === subjects.length && subjects.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                      />
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">Subject Name</th>
                    <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">Code</th>
                    <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">Level</th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subjects.map((subject) => (
                    <tr key={subject.id} className="hover:bg-gray-50/70 transition-colors group">
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(subject.id)}
                          onChange={() => toggleSelectOne(subject.id)}
                          className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                        />
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-medium text-xs sm:text-sm">
                            {subject.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm sm:text-base">{subject.name}</p>
                            {subject.description && (
                              <p className="text-xs text-gray-400 truncate max-w-xs">{subject.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-600 font-mono">
                        {subject.code || '—'}
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4">
                        <span className="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full bg-blue-50 text-blue-700 capitalize">
                          {subject.level || '—'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openAssignModal(subject)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-blue-600 transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-100"
                            title="Assign to Class"
                            aria-label={`Assign ${subject.name} to a class`}
                          >
                            <LinkIcon className="w-4 h-4" />
                            <span className="hidden xl:inline text-xs font-semibold">Assign</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditSubjectModal(subject)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-2.5 py-2 text-amber-600 transition-all hover:-translate-y-0.5 hover:border-amber-200 hover:bg-amber-100"
                            title="Edit Subject"
                            aria-label={`Edit ${subject.name}`}
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span className="hidden xl:inline text-xs font-semibold">Edit</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSubject(subject)}
                            disabled={deleting}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-2.5 py-2 text-red-500 transition-all hover:-translate-y-0.5 hover:border-red-200 hover:bg-red-100 disabled:opacity-50"
                            title="Delete Subject"
                            aria-label={`Delete ${subject.name}`}
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="hidden xl:inline text-xs font-semibold">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ================================ */}
      {/* ADD SUBJECT MODAL */}
      {/* ================================ */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="max-h-[94vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-gray-100 bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                {editingSubject ? 'Edit Subject' : 'Add New Subject'}
              </h2>
              <button
                onClick={closeSubjectModal}
                className="p-1 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            <form onSubmit={handleAddSubject}>
              <div className="space-y-4 sm:space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Subject Name *</label>
                  <input
                    type="text"
                    value={newSubject.name}
                    onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                    placeholder="e.g. Mathematics"
                    required
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Subject Code</label>
                  <input
                    type="text"
                    value={newSubject.code}
                    onChange={(e) => setNewSubject({ ...newSubject, code: e.target.value })}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                    placeholder="e.g. MATH101"
                  />
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Level *</label>
                  <select
                    value={newSubject.level}
                    onChange={(e) => setNewSubject({ ...newSubject, level: e.target.value })}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                  >
                    <option value="nursery">Nursery</option>
                    <option value="primary">Primary</option>
                    <option value="jss">JSS</option>
                    <option value="sss">SSS</option>
                  </select>
                </div>

                {/* Stream */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Stream</label>
                  <select
                    value={newSubject.stream}
                    onChange={(e) => setNewSubject({ ...newSubject, stream: e.target.value })}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                  >
                    <option value="general">General</option>
                    <option value="science">Science</option>
                    <option value="arts">Arts</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>

                {/* Subject Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Subject Type</label>
                  <select
                    value={newSubject.subject_type}
                    onChange={(e) => setNewSubject({ ...newSubject, subject_type: e.target.value })}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                  >
                    <option value="core">Core</option>
                    <option value="elective">Elective</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                  <textarea
                    value={newSubject.description}
                    onChange={(e) => setNewSubject({ ...newSubject, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
                    placeholder="Optional description"
                  />
                </div>

                {/* Is Compulsory */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_compulsory"
                    checked={newSubject.is_compulsory}
                    onChange={(e) => setNewSubject({ ...newSubject, is_compulsory: e.target.checked })}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                  />
                  <label htmlFor="is_compulsory" className="text-sm font-medium text-gray-600">
                    Compulsory subject
                  </label>
                </div>

                {/* Is Active */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={newSubject.is_active}
                    onChange={(e) => setNewSubject({ ...newSubject, is_active: e.target.checked })}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-8">
                <button
                  type="button"
                  onClick={closeSubjectModal}
                  className="flex-1 py-3 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition-colors text-sm sm:text-base"
                  disabled={adding}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base shadow-sm hover:shadow-md"
                >
                  {adding ? (
                    <>
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {editingSubject ? 'Saving...' : 'Adding...'}
                    </>
                  ) : (
                    editingSubject ? 'Save Changes' : 'Add Subject'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================================ */}
      {/* ASSIGN TO CLASS MODAL */}
      {/* ================================ */}
      {showAssignModal && selectedSubject && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-gray-100 bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Assign Subject</h2>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              Assign <strong className="text-primary-700">{selectedSubject.name}</strong> to a class.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Select Class</label>
              <select
                value={selectedClassId}
                onChange={(e) => handleAssignmentClassChange(e.target.value)}
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700 text-sm sm:text-base transition-colors"
              >
                <option value="">-- Select a Class --</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} {cls.section ? `(${cls.section})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedClassId && (
              <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-3">
                {checkingClassSubjects ? (
                  <p className="text-sm text-gray-500">Checking current subjects...</p>
                ) : subjectAlreadyAssigned ? (
                  <div className="flex items-start gap-2 text-sm text-amber-700">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none" />
                    <p>
                      <strong>{selectedSubject.name}</strong> is already assigned
                      to this class. No existing subject will be changed.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-green-700">
                      Ready to add {selectedSubject.name}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      The class currently has {selectedClassSubjects.length}{' '}
                      subject(s).
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition-colors text-sm sm:text-base"
                disabled={assigning}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignToClass}
                disabled={
                  !selectedClassId ||
                  assigning ||
                  checkingClassSubjects ||
                  subjectAlreadyAssigned
                }
                className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base shadow-sm hover:shadow-md"
              >
                {assigning ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Assigning...
                  </>
                ) : (
                  subjectAlreadyAssigned ? 'Already Assigned' : 'Assign Subject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}